import { Boxes, Layers, PackageSearch, Plus, Stethoscope, Tag } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { CatalogItemFormDialog } from './CatalogItemFormDialog';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface CatalogTile {
  to: string;
  title_key: string;
  subtitle_key: string;
  icon: typeof Tag;
  /** Visible when at least one of these permissions is present. */
  any_permission: readonly string[];
}

const TILES: readonly CatalogTile[] = [
  {
    to: '/app/catalog/products',
    title_key: 'catalog.products.title',
    subtitle_key: 'catalog.products.subtitle',
    icon: PackageSearch,
    any_permission: ['catalog.products.read'],
  },
  {
    to: '/app/catalog/services',
    title_key: 'catalog.services.title',
    subtitle_key: 'catalog.services.subtitle',
    icon: Stethoscope,
    any_permission: ['catalog.services.read'],
  },
  {
    to: '/app/catalog/kits',
    title_key: 'kits.title',
    subtitle_key: 'kits.subtitle',
    icon: Boxes,
    any_permission: ['catalog.products.read'],
  },
  {
    to: '/app/catalog/categories',
    title_key: 'catalog.categories.title',
    subtitle_key: 'catalog.categories.subtitle',
    icon: Layers,
    any_permission: ['catalog.products.read'],
  },
  {
    to: '/app/catalog/units',
    title_key: 'catalog.units.title',
    subtitle_key: 'catalog.units.subtitle',
    icon: Tag,
    any_permission: ['catalog.products.read'],
  },
];

export function CatalogIndexPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const is_admin = user?.roles.includes('administrator') ?? false;
  const [creating, setCreating] = useState(false);
  const visible = TILES.filter((tile) =>
    user ? tile.any_permission.some((p) => user.permissions.includes(p)) : false,
  );

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('nav.catalog')}</h2>
          <p className="text-muted-foreground text-sm">{t('catalog.index_subtitle')}</p>
        </div>
        {is_admin ? (
          <Button type="button" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            <span>{t('catalog.item_form.new_item')}</span>
          </Button>
        ) : null}
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((tile) => (
          <Link key={tile.to} to={tile.to} className="block">
            <Card className="hover:border-primary/50 hover:bg-secondary/30 transition-colors">
              <CardHeader className="flex flex-row items-start gap-3">
                <tile.icon className="text-primary mt-1 h-5 w-5" aria-hidden />
                <div>
                  <CardTitle className="text-base">{t(tile.title_key)}</CardTitle>
                  <CardDescription>{t(tile.subtitle_key)}</CardDescription>
                </div>
              </CardHeader>
              <CardContent />
            </Card>
          </Link>
        ))}
      </div>

      {creating ? (
        <CatalogItemFormDialog
          onClose={() => setCreating(false)}
          onSaved={(kind) => {
            setCreating(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('alerts.create_success_body'),
            });
            navigate(kind === 'product' ? '/app/catalog/products' : '/app/catalog/services');
          }}
        />
      ) : null}
    </div>
  );
}
